import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Row, useFlowNotes } from '@/contexts/FlowNotesProvider'
import { useTurns } from '@/contexts/TurnsProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { TurnType } from '@db8/shared'
import { Button } from '@/components/ui/button'
import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { GridCell } from './GridCell'
import { PlusIcon } from 'lucide-react'
import { useHall } from '@/contexts/HallProvider'

export const FlowGrid: React.FC = () => {
  const { isModerator, isDebator } = useHallUsers()
  const { hallMetadata } = useHall()
  const { currentPage, addRow, updateRow, removeRow, updatePage } = useFlowNotes()

  const visibleTurns = hallMetadata?.turns.filter((t) => t.type !== TurnType.Preperatory)
  const isEditable = isModerator || isDebator
  const lastAddedRowId = useRef<string | null>(null)

  const [sortedRowIdsMap, setSortedRowIdsMap] = useState<Record<number, string[]>>({})

  useEffect(() => {
    if (!currentPage) return

    const newMap: Record<number, string[]> = {}

    for (const col of currentPage.columns) {
      newMap[col.id] = col.rows.map((row) => row.id)
    }

    setSortedRowIdsMap(newMap)
  }, [currentPage])

  const handleAddItem = () => {
    if (!currentPage || visibleTurns.length === 0) return

    const newId = `row-${Date.now()}`

    const newRow: Row = {
      id: newId,
      title: '',
      content: '',
    }

    const firstColumnId = visibleTurns[0].step
    addRow(currentPage.id, firstColumnId, newRow)
  }

  const handleDelete = (rowId: string, columnId: number) => {
    if (!currentPage) return

    const column = currentPage.columns.find((c) => c.id === columnId)
    if (!column) return

    const row = column.rows.find((r) => r.id === rowId)
    if (!row) return

    const isEmpty = !row.content.trim()

    if (isEmpty) {
      removeRow(currentPage.id, columnId, rowId)
    } else {
      updateRow(currentPage.id, columnId, rowId, { ...row, content: '' })
    }
  }

  const handleSave = (
    rowId: string,
    columnId: number,
    newContent: { title: string; content: string },
  ) => {
    if (!currentPage) return

    const column = currentPage.columns.find((c) => c.id === columnId)
    if (!column) return

    const row = column.rows.find((r) => r.id === rowId)
    if (!row) return

    updateRow(currentPage.id, columnId, rowId, {
      ...row,
      title: newContent.title,
      content: newContent.content,
    })
  }

  const handleDragEnd = (columnId: number) => (event: any) => {
    const { active, over } = event
    if (!over || active.id === over.id || !currentPage) return

    setSortedRowIdsMap((prevMap) => {
      const ids = prevMap[columnId] || []
      const oldIndex = ids.indexOf(active.id)
      const newIndex = ids.indexOf(over.id)
      const newIds = arrayMove(ids, oldIndex, newIndex)

      const column = currentPage.columns.find((c) => c.id === columnId)
      if (!column) return prevMap

      const rowMap = Object.fromEntries(column.rows.map((r) => [r.id, r]))
      const newRows = newIds.map((id) => rowMap[id]).filter(Boolean)

      updatePage(currentPage.id, {
        columns: currentPage.columns.map((c) => (c.id === columnId ? { ...c, rows: newRows } : c)),
      })

      return {
        ...prevMap,
        [columnId]: newIds,
      }
    })
  }

  const scrollRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const handleScrollSync = (scrollingColumnId: number) => (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop

    Object.entries(scrollRefs.current).forEach(([key, ref]) => {
      const id = parseInt(key)
      if (id !== scrollingColumnId && ref && ref.scrollTop !== scrollTop) {
        ref.scrollTop = scrollTop
      }
    })
  }

  return (
    <div className="w-full h-full flex flex-col overflow-x-auto">
      <div className="flex flex-1 overflow-x-auto noScrollbar">
        {visibleTurns.map((turn) => {
          const sortedRowIds = sortedRowIdsMap[turn.step] || []
          const column = currentPage?.columns.find((c) => c.id === turn.step)
          if (!column) return null

          const rows = sortedRowIds
            .map((id) => column.rows.find((r) => r.id === id))
            .filter((r): r is typeof r => Boolean(r))

          const handleAddRowToColumn = () => {
            const newRow: Row = {
              id: `row-${Date.now()}`,
              title: '',
              content: '',
            }
            lastAddedRowId.current = newRow.id
            addRow(currentPage.id, turn.step, newRow)
          }

          return (
            <div
              key={turn.step}
              className="flex flex-col border-l border-faint dark:border-gray-700 flex-1 min-w-[300px] max-w-[1fr]"
            >
              <div className="border border-faint dark:border-gray-700 p-2 text-center font-semibold text-sm sm:text-base truncate">
                {turn.abbreviation}
              </div>

              <div
                // @ts-ignore
                ref={(el) => (scrollRefs.current[turn.step] = el)}
                onScroll={handleScrollSync(turn.step)}
                className="flex-1 overflow-y-auto noScrollbar space-y-1 p-2"
              >
                <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd(turn.step)}>
                  <SortableContext items={sortedRowIds} strategy={verticalListSortingStrategy}>
                    {rows.map((row) => (
                      <GridCell
                        key={row.id}
                        id={row.id}
                        row={row}
                        columnId={turn.step}
                        content={row.content}
                        title={row.title}
                        onSave={(rowId, newContent) => handleSave(rowId, turn.step, newContent)}
                        onDelete={() => handleDelete(row.id, turn.step)}
                        autoFocus={row.id === lastAddedRowId.current}
                        currentPage={currentPage}
                      />
                    ))}
                  </SortableContext>
                </DndContext>

                {isEditable && (
                  <div className="flex justify-center pt-3">
                    <Button
                      size="icon"
                      variant="secondary"
                      className="dark:bg-secondary dark:hover:bg-gray-700"
                      onClick={handleAddRowToColumn}
                      title="Add new item to this column"
                    >
                      <PlusIcon size={18} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
